import css from './dashboards.module.css'
import ResizableByDrag from '@/shared/Common/ResizableByDrag'
import DashboardGroupsTree from '@/pages/Dashboards/components/DashboardGroupsTree'
import useTreeSelectCtrl from '@/shared/Tree/ctrl/useTreeSelectCtrl.ts'
import { IParameterGroup } from '@/pages/Parameters/parameters.types.ts'
import MainLayout from '@/shared/Layouts/MainLayout'
import useDashboardGroup from '@/pages/Dashboards/hooks/useDashboardGroup.ts'
import Tabs from '@/shared/Tabs'
import { useRecoilState } from 'recoil'
import dashboardsAtom from '@/pages/Dashboards/dashboards.atom.ts'
import CreateDashboardTile from '@/pages/Dashboards/components/DashboardTile/CreateDashboardTile'
import useDashboards from '@/pages/Dashboards/hooks/useDashboards.ts'
import DashboardTile from '@/pages/Dashboards/components/DashboardTile'
import OrganizationIcon from '@/assets/icons/organization.svg?react'
import BreadcrumbPaths from '@/shared/BreadcrumbPaths'
import { useMemo } from 'react'
import breadcrumbPathsUtils from '@/shared/BreadcrumbPaths/breadcrumbPaths.utils.ts'
import useDashboardGroups from '@/pages/Dashboards/hooks/useDashboardGroups.ts'
import SearchInput from '@/shared/Inputs/SearchInput'
import useTitle from '@/shared/hooks/useTitle'

const Dashboards = () => {
  const treeSelectCtrl = useTreeSelectCtrl<IParameterGroup['id']>('dashboards-groups-tree', null)
  const group = useDashboardGroup(treeSelectCtrl.selectedItem)
  const dashboards = useDashboards().dashboards
  const groups = useDashboardGroups().groups
  useTitle(group?.name ? `Дашборды — ${group.name}` : 'Дашборды')

  const [state, setState] = useRecoilState(dashboardsAtom)

  const handleFilterChange = (val: any, name: string) => {
    setState((prev) => ({ ...prev, filters: { ...prev.filters, [name]: val } }))
  }

  const selectedGroupBreadcrumbsPaths = useMemo(() => {
    if (!group) return []
    return breadcrumbPathsUtils.getPathsGroup(group.id, groups, (group) => group.name)
  }, [groups, group])

  const filteredDashboards = useMemo(() => {
    const filteredByGroup = dashboards.filter((d) => d.groupId === treeSelectCtrl.selectedItem)

    const filteredSharedWithOrganizationValue = state.filters.type === 'shared'
    const filteredByType = filteredByGroup.filter(
      (d) => d.sharedWithOrganization === filteredSharedWithOrganizationValue
    )

    if (!state.filters.search) {
      return filteredByType
    }

    return filteredByType.filter((d) => d.name.toLowerCase().includes(state.filters.search.toLowerCase()))
  }, [treeSelectCtrl.selectedItem, dashboards, state.filters.type, state.filters.search])

  return (
    <MainLayout>
      <div className={css.wrapper}>
        <div className={css.mainCard}>
          <div className="inline-flex-gap gap24 height100">
            <ResizableByDrag initialWidth={280} resizerClassname={css.resizer} wrapperClassname={css.resizerWrapper}>
              <DashboardGroupsTree treeSelectCtrl={treeSelectCtrl as any} />
            </ResizableByDrag>
            <div className="flex-lines gap24 width100">
              <div className="flex-lines gap8 width100">
                <div className="flex-lines gap16">
                  <BreadcrumbPaths
                    paths={selectedGroupBreadcrumbsPaths}
                    styleLastPath={{ color: 'var(--text-description)' }}
                  />
                  <h3 className="text-heading-3">{group?.name}</h3>
                </div>
                <Tabs
                  tabs={[
                    { title: 'Персональные', id: 'personal' },
                    {
                      title: (
                        <span className="text-nm inline-flex-gap gap8 center" style={{ color: 'inherit' }}>
                          <OrganizationIcon style={{ width: '16px', height: '16px', color: 'inherit' }} />
                          <span>Организация</span>
                        </span>
                      ),
                      id: 'shared',
                    },
                  ]}
                  onSelect={(t) => handleFilterChange(t, 'type')}
                  selectedTab={state.filters.type}
                  style={{ width: '100%' }}
                />
              </div>
              <SearchInput
                inputStyle={{ width: '350px' }}
                value={state.filters.search}
                name="search"
                onChange={handleFilterChange}
                placeHolder="Поиск по дашбордам"
              />
              <div className="inline-flex-gap gap24" style={{ flexWrap: 'wrap' }}>
                <CreateDashboardTile groupId={treeSelectCtrl.selectedItem} />
                {filteredDashboards.map((d) => (
                  <DashboardTile dashboardId={d.id} key={d.id} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  )
}

export default Dashboards
